import "./FactoryInventory.css";
import React, { useState, useEffect, useMemo } from "react";
import Table from "../../components/Table/table";
import Loading from "../../components/Loading/loading";
import { Link } from "react-router-dom";
import { utils } from "react-modern-calendar-datepicker";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import "react-multi-date-picker/styles/layouts/mobile.css";

const INVENTORY_API_URL = "/api/getfactorydata";
const CURRENCY_API_URL = "/api/getcurrencypricesdata";

function toFarsiDigits(num) {
  if (num === null || num === undefined) return "";
  const farsiDigits = ['۰','۱','۲','۳','۴','۵','۶','۷','۸','۹'];
  return num.toString().replace(/[0-9]/g, x => farsiDigits[x]);
}

const transformInventory = (data) => {
  const factories = {};

  data.forEach((item) => {
    const { factoryName, seller, price, source, url, size, seller_abb } = item;
    // Create a unique key for the product variant
    const key = `${seller}|${price}|${source}|${url}`;

    if (!factories[factoryName]) {
      factories[factoryName] = {};
    }
    if (!factories[factoryName][key]) {
      factories[factoryName][key] = {
        title: seller_abb || seller,
        description: [], // Will hold sizes
        url,
        price,
        source,
      };
    }
    factories[factoryName][key].description.push(size);
  });

  return Object.entries(factories).map(([factoryName, groups]) => {
    const allProducts = Object.values(groups).map((group) => ({
      ...group,
      // FIX HERE: Use [...new Set(...)] to remove duplicates
      description: [...new Set(group.description)]
        .sort((a, b) => a - b) 
        .join(","), 
    }));

    // Pre-calculate priced vs unpriced lists
    const pricedProducts = allProducts.filter(
      (p) => p.price && Number(p.price) > 0
    );
    const unpricedProducts = allProducts.filter(
      (p) => !p.price || Number(p.price) === 0
    );

    return {
      factoryName,
      pricedProducts,
      unpricedProducts,
    };
  });
};

const FactoriesInventory = () => {
  const [factoriesData, setFactoriesData] = useState([]);
  const [lastCurrency, setLastCurrency] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tracks which factories are expanded to show unpriced items
  const [factoriesShowingUnpriced, setFactoriesShowingUnpriced] = useState([]);

  // Date selection
  const [selectedDay, setSelectedDay] = useState(utils("fa").getToday());

  // --- 1. Helper: Format Date ---
  const formatToAPIDate = (jalaliDate) =>
    `${jalaliDate.year}-${String(jalaliDate.month).padStart(2, "0")}-${String(
      jalaliDate.day
    ).padStart(2, "0")}`;

  // --- 2. Logic: Process Data for Display (Sorting & Filtering) ---
  const processedFactories = useMemo(() => {
    if (!factoriesData || factoriesData.length === 0) return [];

    const mappedFactories = factoriesData.map(factory => {
      const isUnpricedVisible = factoriesShowingUnpriced.includes(factory.factoryName);
      const pricedCount = factory.pricedProducts.length;
      
      let productsToShow;

      if (pricedCount > 0) {
        // Case A: Has prices. Toggle logic applies.
        productsToShow = isUnpricedVisible
          ? [...factory.pricedProducts, ...factory.unpricedProducts]
          : factory.pricedProducts;
      } else {
        // Case B: No prices. Always show unpriced items.
        productsToShow = factory.unpricedProducts;
      }
      
      // If no products result from filtering, hide the factory entirely
      if (productsToShow.length === 0) return null;

      return {
        ...factory,
        productsToShow,
        isUnpricedVisible,
        hasPricedProducts: pricedCount > 0, // Used for sorting
      };
    }).filter(Boolean); // Remove nulls

    // Sort: Factories with prices go first, then alphabetical
    return mappedFactories.sort((a, b) => {
      if (a.hasPricedProducts && !b.hasPricedProducts) return -1; 
      if (!a.hasPricedProducts && b.hasPricedProducts) return 1;  
      return a.factoryName.localeCompare(b.factoryName);
    });
    
  }, [factoriesData, factoriesShowingUnpriced]);

  // --- 3. Handler: Table Title Click ---
  const handleTitleClick = (factoryName) => {
    const factory = factoriesData.find(f => f.factoryName === factoryName);
    
    // Safety check: Don't toggle if there are no priced products (logic handled in render too)
    if (!factory || factory.pricedProducts.length === 0) return;
    
    setFactoriesShowingUnpriced(prev => {
        if (prev.includes(factoryName)) return prev.filter(n => n !== factoryName);
        return [...prev, factoryName];
    });
  }

  // --- 4. Effect: Fetch Currency (Every 60 Seconds) ---
  useEffect(() => {
    const fetchCurrency = () => {
      fetch(CURRENCY_API_URL)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.length > 0) setLastCurrency(data[data.length - 1]);
        })
        .catch(console.error);
    };

    fetchCurrency(); 
    const interval = setInterval(fetchCurrency, 60000); 
    return () => clearInterval(interval);
  }, []);

  // --- 5. Effect: Fetch Inventory (Date Change OR Every 60 Seconds) ---
  useEffect(() => {
    if (!selectedDay) return;

    // isBackground = true means "Silent Update" (no spinner)
    const fetchData = async (isBackground = false) => {
      if (!isBackground) setIsLoading(true);
      setError(null);
      
      const formattedDate = formatToAPIDate(selectedDay);
      const urlWithDate = `${INVENTORY_API_URL}?date=${formattedDate}`;
      
      try {
        const response = await fetch(urlWithDate);
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const jsonData = await response.json();
        const transformed = transformInventory(jsonData); 
        setFactoriesData(transformed);
      } catch (e) {
        console.error("Failed to fetch data:", e);
        // Only block the UI with an error if it's the user's manual action
        if (!isBackground) {
            setError("Failed to load inventory data for the selected date.");
            setFactoriesData([]); 
        }
      } finally {
        if (!isBackground) setIsLoading(false);
      }
    };

    // A. Initial Load (Shows Spinner)
    fetchData(false); 

    // B. Interval Load (Silent - Every 60s)
    const interval = setInterval(() => fetchData(true), 60000);

    // C. Cleanup
    return () => clearInterval(interval);
  }, [selectedDay]);

  // --- 6. Handler: Date Picker ---
  const handleDateChange = (dateObject) => {
    if (!dateObject) return;
    setSelectedDay({
      year: Number(dateObject.year),
      month: Number(dateObject.month.number || dateObject.month),
      day: Number(dateObject.day),
    });
  };

  if (isLoading) return <Loading />;
  if (error && factoriesData.length === 0) return <p className="dashboard-message error">{error}</p>;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Link to="/home" className="header-link">خانه</Link>
        <p className="page-title">موجودی مبدا</p>
        
        <div className="header-right-group">
          <div className="date-picker-wrapper">
            <DatePicker
              value={`${selectedDay.year}/${selectedDay.month}/${selectedDay.day}`}
              onChange={handleDateChange}
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-right" 
              inputClass="custom-date-input" 
            />
          </div>
        </div>
      </div>

      {/* Inline error for background update failures (optional) */}
      {error && <p className="dashboard-message error-inline">{error}</p>}
      
      <section>
        <h2 className="section-title">میلگرد</h2>
        {processedFactories.length === 0 ? (
          <p className="dashboard-message">هیچ کالایی برای تاریخ انتخاب شده یافت نشد.</p>
        ) : (
          <div className="grid-container">
            {processedFactories.map((factory) => (
              <Table
                key={factory.factoryName}
                title={"کارخانه " + factory.factoryName}
                products={factory.productsToShow}
                
                // Only pass the click handler if the factory actually has priced products
                onTitleClick={
                  factory.hasPricedProducts 
                    ? () => handleTitleClick(factory.factoryName) 
                    : undefined
                }
                
                isUnpricedShown={factory.isUnpricedVisible}
              />
            ))}
          </div>
        )}
      </section>
      
      <div className="floating-footer">
        <p className="pill">
          سکه: {lastCurrency ? toFarsiDigits((lastCurrency.gold_coin_price / 10).toLocaleString()) : "..."}
        </p>
        <p className="pill">
          دلار: {lastCurrency ? toFarsiDigits((lastCurrency.dollar_price / 10).toLocaleString()) : "..."}
        </p>
        <p className="pill">
          طلا: {lastCurrency ? toFarsiDigits((lastCurrency.gold_gram_price / 10).toLocaleString()) : "..."}
        </p>
        <p className="pill">
          بورس: {lastCurrency ? toFarsiDigits(lastCurrency.stock_index.toLocaleString()) : "..."}
        </p>
        <p className="pill pill-ons">
          انس: {lastCurrency && lastCurrency.ounce_price ? toFarsiDigits(lastCurrency.ounce_price.toLocaleString()) : "..."}
        </p>
      </div>
    </div>
  );
};

export default FactoriesInventory;
